import * as React from 'react';
import Pusher from 'pusher-js';
import moment from 'moment';

import {
  DataTable,
  DropdownInput,
  EXTRA_SMALL_ICON_BASE_SRC,
  Snackbar,
  Spinner,
  Tooltip,
  defaultSnackbarTimeout,
  ADMIN,
  NOT_APPLICABLE,
  PENDING,
  TEACHER,
  SKIPPED,
  APPROVED,
  DENIED,
  getPageTitle,
} from '../../Shared/index';
import useSnackbarMonitor from '../../Shared/hooks/useSnackbarMonitor';
import {
  FULL,
  RESTRICTED,
  RESTRICTED_TEXT,
  restrictedElement,
} from '../shared';
import AdminActionModal from '../components/admin_action_modal';
import CreateAndLinkAccountsModal from '../components/create_and_link_accounts_modal';
import BulkArchiveClassesModal from '../components/bulk_archive_classes_modal';
import { requestGet, requestPost } from '../../../modules/request/index';
import { AccountManagementModel, School, Teacher, } from '../interfaces'

const arrowRightIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/arrow-right.svg`} />
const checkIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/check.svg`} />
const clockIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/clock.svg`} />
const exclamationIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/exclamation.svg`} />
const helpIcon = <img alt="Question mark inside of circle" src={`${EXTRA_SMALL_ICON_BASE_SRC}/help.svg`} />
const xmarkIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/xmark.svg`} />

const VERIFY_TEACHERS_ACTION = 'verifyTeachersAction'
const ARCHIVE_CLASSES_ACTION = 'archiveClassesAction'
const ROLE_AND_VERIFICATION_STATUS_STRING_FOR_SORT = 'roleAndVerificationStatusStringForSort'
const CLASSLINK = 'ClassLink'
const CLEVER = 'Clever'

const teacherColumns = [
  {
    name: 'Name',
    attribute: 'name',
    isSortable: true,
    width: '170px',
    noTooltip: true,
    rowSectionClassName: 'name border-right',
    renderCellsAsHeaders: true
  },
  {
    name: 'Role',
    rowSectionClassName: 'role',
    attribute: 'role',
    isSortable: true,
    sortAttribute: ROLE_AND_VERIFICATION_STATUS_STRING_FOR_SORT,
    width: '170px'
  },
  {
    name: 'Email',
    rowSectionClassName: 'email',
    attribute: 'email',
    isSortable: true,
    width: '170px'
  },
  {
    name: 'Students',
    attribute: 'number_of_students',
    isSortable: true,
    width: '119px',
  },
  {
    name: 'Last Sign-in',
    attribute: 'lastSignIn',
    isSortable: true,
    sortAttribute: 'last_sign_in',
    headerClassName: 'eight-px-right-padding',
    width: '120px',
  },
  {
    name: (
      <span className="header-with-tooltip">
        Verification Status
        <Tooltip
          skipPositioning={true}
          tooltipText="Indicates whether a user has been confirmed to work at this school. Approved users can access Diagnostic Insights."
          tooltipTriggerText={helpIcon}
        />
      </span>
    ),
    attribute: 'verificationStatus',
    width: '170px',
    headerClassName: 'eight-px-right-padding',
    rowSectionClassName: 'eight-px-right-padding verification-status',
    sortAttribute: 'verificationStatusStringForSort',
    isSortable: true,
    textAlign: 'left',
    noTooltip: true,
    ariaLabel: 'Verification Status'
  },
  {
    name: (
      <span className="header-with-tooltip">
        Reviewed By
        <Tooltip
          skipPositioning={true}
          tooltipText="Shows who reviewed and made a decision on the user's verification, such as the Quill team or an admin."
          tooltipTriggerText={helpIcon}
        />
      </span>
    ),
    attribute: 'reviewerName',
    width: '114px',
    headerClassName: 'eight-px-right-padding',
    rowSectionClassName: 'eight-px-right-padding reviewer-name',
    sortAttribute: 'reviewerName',
    isSortable: true,
    textAlign: 'left',
    ariaLabel: 'Reviewed By'
  },
  {
    name: 'Actions',
    attribute: 'actions',
    isActions: true
  }
];

export interface AccountManagementProps {
  accessType: string;
  adminId: number;
  passedModel?: AccountManagementModel;
  passedSelectedSchoolId?: number;
}

enum modalNames {
  createAndLinkAccountsModal = 'createAndLinkAccountsModal',
  makeAdminModal = 'makeAdminModal',
  removeAdminModal = 'removeAdminModal',
  bulkArchiveClassesModal = 'bulkArchiveClassesModal'
}

interface Action {
  name: string;
  action: (id: number) => void;
}

export const AccountManagement: React.FC<AccountManagementProps> = ({
  accessType,
  adminId,
  passedModel,
  passedSelectedSchoolId,
}) => {
  document.title = getPageTitle("Premium Hub", "Account Management")
  const [checkedIds, setCheckedIds] = React.useState<number[]>([]);
  const [error, setError] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(passedModel ? false : true);
  const [model, setModel] = React.useState<AccountManagementModel>(
    passedModel || { teachers: [], schools: [], admin_approval_requests: [] }
  );
  const [showModal, setShowModal] = React.useState<modalNames | null>(null);
  const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
  const [snackbarText, setSnackbarText] = React.useState<string>('');
  const [selectedSchoolId, setSelectedSchoolId] = React.useState<number | null>(passedSelectedSchoolId || null);
  const [userIdForModal, setUserIdForModal] = React.useState<number | null>(null);

  const selectedSchoolIdRef = React.useRef<number | null>(selectedSchoolId);

  React.useEffect(() => {
    initializePusher(false);
    getData();
  }, []);

  React.useEffect(() => {
    selectedSchoolIdRef.current = selectedSchoolId;
  }, [selectedSchoolId]);

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout);

  function getData(skipLoading: boolean = false): void {
    requestGet(
      `${window.postbuildEnv.DEFAULT_URL}/admins/${adminId}`,
      (body: any) => {
        receiveData(body, skipLoading);
      }
    );
  }

  function receiveData(data: Partial<AccountManagementModel>, skipLoading: boolean): void {
    if (Object.keys(data).length > 1) {
      setModel(data as AccountManagementModel);
      if (!selectedSchoolIdRef.current) {
        const defaultSchool =
          data.schools?.find((s: School) => s.id === data.associated_school?.id) || data.schools?.[0];
        setSelectedSchoolId(defaultSchool?.id || null);
      }
      setLoading(false);
    } else if (!skipLoading) {
      setModel(data as AccountManagementModel);
      setLoading(true);
    }
  }

  function initializePusher(skipLoading: boolean): void {
    if (window.postbuildEnv.RAILS_ENV === 'development') {
      Pusher.logToConsole = true;
    }
    const pusher = new Pusher(window.postbuildEnv.PUSHER_KEY, {
      cluster: window.postbuildEnv.PUSHER_CLUSTER,
    });
    const channel = pusher.subscribe(String(adminId));
    channel.bind('admin-users-found', () => {
      getData(skipLoading);
    });
  }

  function resetCheckedIds() { setCheckedIds([]) }

  function addTeacherAccountSuccess(message: string): void {
    getData(true);
    setSnackbarText(message);
    setShowSnackbar(true);
    closeModal();
  }

  function handleClickCreateAndLinkAccountsButton(): void {
    if (accessType === RESTRICTED) {
      setSnackbarText(RESTRICTED_TEXT);
      setShowSnackbar(true);
    } else {
      setShowModal(modalNames.createAndLinkAccountsModal);
    }
  }

  function handleUserAction(link: string, data: any, callback: Function=null): void {
    setError('');
    requestPost(
      link,
      data,
      (response: any) => {
        getData(true);
        setSnackbarText(response.message);
        setShowSnackbar(true);
        if (callback) { callback() }
      },
      (response: any) => {
        if (response.error) {
          setError(response.error);
        } else {
          // to do, use Sentry to capture error
        }
      }
    );
  }

  function onChangeSelectedSchool(selectedSchoolOption: { label: string; value: number }): void {
    setSelectedSchoolId(selectedSchoolOption.value);
  }

  function closeModal(): void {
    setUserIdForModal(null);
    setShowModal(null);
  }

  function loginAsUser(id: number): void {
    if (accessType === FULL) {
      window.location.href = `/users/${id}/admin_sign_in_classroom_manager`;
    } else {
      setSnackbarText(RESTRICTED_TEXT);
      setShowSnackbar(true);
    }
  }

  function viewPremiumReports(id: number): void {
    if (accessType === FULL) {
      window.location.href = `/users/${id}/admin_sign_in_progress_reports`;
    } else {
      setSnackbarText(RESTRICTED_TEXT);
      setShowSnackbar(true);
    }
  }

  function resendLoginDetailsForTeacher(id: number): void {
    handleUserAction(`/users/${id}/admin_resend_login_details`, {
      role: 'teacher',
      school_id: selectedSchoolId,
    });
  }

  function unlinkFromSchool(id: number): void {
    handleUserAction(`/users/${id}/admin_unlink_from_school`, { role: 'teacher' });
  }

  function approveAdminRequest(id: number): void {
    handleUserAction(`/users/${id}/approve_admin_request`, { school_id: selectedSchoolId });
  }

  function denyAdminRequest(id: number): void {
    handleUserAction(`/users/${id}/deny_admin_request`, { school_id: selectedSchoolId });
  }

  function resendLoginDetailsForAdmin(id: number): void {
    handleUserAction(`/users/${id}/admin_resend_login_details`, {
      role: 'admin',
      school_id: selectedSchoolId,
    });
  }

  function handleConfirmMakeAdmin(): void {
    handleUserAction(`/users/${userIdForModal}/admin_make_admin`, {
      school_id: selectedSchoolId,
    });
    closeModal();
  }

  function handleConfirmRemoveAsAdmin(): void {
    handleUserAction(`/users/${userIdForModal}/admin_remove_as_admin`, {
      school_id: selectedSchoolId,
    });
    closeModal();
  }

  function approveTeacherVerification(teacherIds: number[], callback: Function=null): void {
    handleUserAction(`/admins/approve_teacher_verifications`, { teacher_ids: teacherIds }, callback);
  }

  function denyTeacherVerification(teacherIds: number[]): void {
    handleUserAction(`/admins/deny_teacher_verifications`, { teacher_ids: teacherIds });
  }

  function removeAsAdmin(id: number): void {
    setUserIdForModal(id);
    setShowModal(modalNames.removeAdminModal);
  }

  function makeAdmin(id: number): void {
    setUserIdForModal(id);
    setShowModal(modalNames.makeAdminModal);
  }

  function openBulkArchiveClassesModal() {
    setShowModal(modalNames.bulkArchiveClassesModal)
  }

  function checkRow(id: number): void {
    const newIds = checkedIds.concat(id);
    setCheckedIds(newIds);
  }

  function uncheckRow(id: number): void {
    const newIds = checkedIds.filter((checkedId) => checkedId !== id);
    setCheckedIds(newIds);
  }

  function checkAllRows(): void {
    const newIds = filteredData.map((user) => user.id);
    setCheckedIds(newIds);
  }

  function uncheckAllRows(): void {
    setCheckedIds([]);
  }

  const actionsHash = {
    verifyTeacher: {
      name: 'Verify teacher',
      action: (id) => approveTeacherVerification([id])
    },
    approveVerificationRequest: {
      name: 'Approve verification request',
      action: (id) => approveTeacherVerification([id])
    },
    denyVerificationRequest: {
      name: 'Deny verification request',
      action: (id) => denyTeacherVerification([id])
    },
    resendLoginDetailsAdmin: {
      name: 'Resend login details',
      action: (id) => resendLoginDetailsForAdmin(id)
    },
    resendLoginDetailsTeacher: {
      name: 'Resend login details',
      action: (id) => resendLoginDetailsForTeacher(id)
    },
    loginAsAdmin: {
      name: 'Login as admin',
      action: (id) => loginAsUser(id)
    },
    loginAsTeacher: {
      name: 'Login as teacher',
      action: (id) => loginAsUser(id)
    },
    viewPremiumReports: {
      name: 'View premium reports',
      action: (id) => viewPremiumReports(id)
    },
    approveAdminRequest: {
      name: 'Approve admin request',
      action: (id) => approveAdminRequest(id)
    },
    denyAdminRequest: {
      name: 'Deny admin request',
      action: (id) => denyAdminRequest(id)
    },
    removeAsAdmin: {
      name: 'Remove as admin',
      action: (id) => removeAsAdmin(id)
    },
    makeAdmin: {
      name: 'Make admin',
      action: (id) => makeAdmin(id)
    },
    unlinkFromSchool: {
      name: 'Unlink from school',
      action: (id) => unlinkFromSchool(id)
    },
  }

  function onSelectAction(selectedOption: { label: string; value: string }): void {
    if (selectedOption.value === VERIFY_TEACHERS_ACTION) {
      approveTeacherVerification(checkedIds, resetCheckedIds);
    } else if (selectedOption.value === ARCHIVE_CLASSES_ACTION) {
      openBulkArchiveClassesModal();
    }
  }

  function handleBulkArchiveClasses(date: Date): void {
    handleUserAction(
      `/admins/bulk_archive_classes`,
      { teacher_ids: checkedIds, filter_date: date },
      resetCheckedIds
    );
    closeModal();
  }

  function actionsForUser(user: Teacher, role: string): Action[] {
    const { teacher_verification_status, last_sign_in } = user;
    let actions: Action[] = [];

    if (role === ADMIN) {
      actions = last_sign_in ? [] : [actionsHash.resendLoginDetailsAdmin];
      actions = actions.concat([
        actionsHash.loginAsAdmin,
        actionsHash.viewPremiumReports,
        actionsHash.removeAsAdmin,
      ]);
    } else if (pendingAdminRequest(user, role)) {
      actions = last_sign_in ? [] : [actionsHash.resendLoginDetailsTeacher];
      actions = actions.concat([
        actionsHash.loginAsTeacher,
        actionsHash.viewPremiumReports,
        actionsHash.approveAdminRequest,
        actionsHash.denyAdminRequest,
        actionsHash.unlinkFromSchool,
      ]);
    } else {
      actions = last_sign_in ? [] : [actionsHash.resendLoginDetailsTeacher];
      if (teacher_verification_status === PENDING) {
        actions = actions.concat([actionsHash.approveVerificationRequest, actionsHash.denyVerificationRequest])
      } else if (teacher_verification_status !== APPROVED) {
        actions = actions.concat([actionsHash.verifyTeacher])
      }
      actions = actions.concat([
        actionsHash.loginAsTeacher,
        actionsHash.viewPremiumReports,
        actionsHash.makeAdmin,
        actionsHash.unlinkFromSchool,
      ]);
    }
    return actions;
  }

  function pendingAdminRequest(user: Teacher, role: string) {
    return (
      role === TEACHER &&
      user.admin_info?.approval_status === PENDING &&
      model.admin_approval_requests.find(
        (request) => request.admin_info_id === user.admin_info?.id
      )
    );
  }

  if (loading) {
    return (
      <div className="account-management white-background-accommodate-footer">
        <div className="container">
          <div className="header">
            <h1>Account Management</h1>
          </div>
          <Spinner />
        </div>
      </div>
    );
  }

  const schoolOptions = model.schools.map((school) => ({
    label: school.name,
    value: school.id,
  }));

  const renderReviewerName = (user: Teacher, role: string): string | JSX.Element | undefined => {
    const { admin_reviewer_name, teacher_reviewer_name } = user;
    if (role === ADMIN) { return admin_reviewer_name }

    if ([CLEVER, CLASSLINK].includes(teacher_reviewer_name)) {
      return (
        <Tooltip
          skipPositioning={true}
          tooltipText={`Verified automatically via ${teacher_reviewer_name === CLEVER ? 'Clever Secure Sync' : teacher_reviewer_name} rostering.`}
          tooltipTriggerText={teacher_reviewer_name}
        />
      )
    }

    return role === ADMIN ? admin_reviewer_name : teacher_reviewer_name;
  };

  const determineVerificationStatus = (user: Teacher, role: string): React.ReactNode => {
    const { teacher_verification_status, admin_verification_status } = user;
    const verificationStatus = role === ADMIN ? admin_verification_status : teacher_verification_status;

    if (verificationStatus === APPROVED || (role === ADMIN && !verificationStatus)) {
      return {
        verificationStatus: (
          <div>
            {checkIcon}
            Approved
          </div>
        ),
        verificationStatusStringForSort: `3 ${APPROVED}`,
      };
    } else if (verificationStatus === DENIED) {
      return {
        verificationStatus: (
          <div>
            {xmarkIcon}
            Denied
          </div>
        ),
        verificationStatusStringForSort: `5 ${DENIED}`,
      }
    } else if (verificationStatus === SKIPPED) {
      return {
        verificationStatus: (
          <div>
            {arrowRightIcon}
            Skipped
          </div>
        ),
        verificationStatusStringForSort: `4 ${SKIPPED}`,
      }
    } else if (verificationStatus === PENDING) {
      return {
        verificationStatus: (
          <React.Fragment>
            <div className="extended-height">
              {clockIcon}
              Requested verification
            </div>
            <Tooltip
              skipPositioning={true}
              tooltipText={`This ${role} has requested to be verified as a ${role} at this school. Use the Actions menu to approve or deny their request.`}
              tooltipTriggerText={helpIcon}
            />
          </React.Fragment>
        ),
        verificationStatusStringForSort: '1 Requested Verification',
      }
    } else if (role === TEACHER && !verificationStatus) {
      return {
        verificationStatus: (
          <React.Fragment>
            <div className="extended-height">
              {exclamationIcon}
              Needs verification
            </div>
            <Tooltip
              skipPositioning={true}
              tooltipText="This teacher hasn’t requested to be verified, but you can proactively verify them as a teacher at this school. Use the Actions menu to do so."
              tooltipTriggerText={helpIcon}
            />
          </React.Fragment>
        ),
        verificationStatusStringForSort: '2 Needs Verification',
      }
    }
  };

  const filteredData = model.teachers
    .filter((d) => d.schools.find((s) => s.id === selectedSchoolId))
    .map((user) => {
      const relevantSchool = user.schools.find((s) => s.id === selectedSchoolId);
      const lowerCasedRole = relevantSchool?.role.toLowerCase() || '';
      const { verificationStatus, verificationStatusStringForSort, } = determineVerificationStatus(user, lowerCasedRole)
      user.actions = actionsForUser(user, lowerCasedRole);
      const adminRequest = pendingAdminRequest(user, lowerCasedRole);
      if (adminRequest) {
        user.role = adminRequest.request_made_during_sign_up
          ? '🛎️ New user requested to become admin'
          : '🛎️ Teacher requested admin role';
      } else {
        user.role = relevantSchool?.role;
      }
      user[ROLE_AND_VERIFICATION_STATUS_STRING_FOR_SORT] = `${adminRequest ? 'adminrequest' : lowerCasedRole}-${verificationStatusStringForSort}`
      user.lastSignIn = user.last_sign_in
        ? moment(user.last_sign_in).format('MMM DD, YYYY')
        : NOT_APPLICABLE;
      user.reviewerName = renderReviewerName(user, lowerCasedRole) || '—';
      user.checked = checkedIds.includes(user.id);
      user.verificationStatus = verificationStatus
      user.verificationStatusStringForSort = verificationStatusStringForSort
      return user;
    });

  const userNameForModal = model.teachers.find((user) => user.id === userIdForModal)?.name;
  const schoolNameForModal = model.schools.find((s) => s.id === selectedSchoolId)?.name;

  const renderCreateAndLinkButtonOrActionsDropdown = (): React.ReactNode => {
    if (checkedIds.length) {
      const dropdownActions = [
        { label: 'Verify teachers', value: VERIFY_TEACHERS_ACTION },
        { label: 'Archive classes', value: ARCHIVE_CLASSES_ACTION }
      ];
      return (
        <DropdownInput
          className="actions-dropdown small bordered focus-on-light"
          handleChange={onSelectAction}
          id="select-action"
          label="Action"
          labelAdjacentElement={(
            <Tooltip
              tooltipText="Bulk actions are currently limited to verifying teachers. All other actions must be done one user at a time."
              tooltipTriggerText={helpIcon}
            />
          )}
          options={dropdownActions}
          placeholder="Select Action"
          value={null}
        />
      );
    } else {
      return (
        <button
          className="focus-on-light green quill-button contained small"
          onClick={handleClickCreateAndLinkAccountsButton}
          type="button"
        >
          Create and link accounts
        </button>
      );
    }
  };

  let accountManagementContent: React.ReactNode = (
    <React.Fragment>
      <div className="page-level-actions">
        <DropdownInput
          className="school-dropdown small bordered focus-on-light"
          handleChange={onChangeSelectedSchool}
          id="school"
          isSearchable={true}
          label="School"
          options={schoolOptions}
          value={schoolOptions.find((so) => so.value === selectedSchoolId)}
        />
        {renderCreateAndLinkButtonOrActionsDropdown()}
      </div>
      <div className="data-table-wrapper">
        <DataTable
          checkAllRows={checkAllRows}
          checkRow={checkRow}
          defaultSortAttribute={ROLE_AND_VERIFICATION_STATUS_STRING_FOR_SORT}
          headers={teacherColumns}
          reportingFormat={true}
          rows={filteredData}
          showActions={true}
          showCheckboxes={true}
          uncheckAllRows={uncheckAllRows}
          uncheckRow={uncheckRow}
        />
      </div>
    </React.Fragment>
  );

  if (accessType === RESTRICTED) {
    accountManagementContent = restrictedElement;
  }

  return (
    <main className="account-management white-background">
      <div className="container">
        <Snackbar text={snackbarText} visible={showSnackbar} />
        {showModal === modalNames.makeAdminModal && (
          <AdminActionModal
            bodyText={`Are you sure you want to add ${userNameForModal} as an admin of ${schoolNameForModal}?`}
            handleClickConfirm={handleConfirmMakeAdmin}
            handleCloseModal={closeModal}
            headerText="Add admin?"
          />
        )}
        {showModal === modalNames.removeAdminModal && (
          <AdminActionModal
            bodyText={`Are you sure you want to remove ${userNameForModal} as an admin of ${schoolNameForModal}?`}
            handleClickConfirm={handleConfirmRemoveAsAdmin}
            handleCloseModal={closeModal}
            headerText="Remove admin?"
          />
        )}
        {showModal === modalNames.createAndLinkAccountsModal && (
          <CreateAndLinkAccountsModal
            addTeacherAccountSuccess={addTeacherAccountSuccess}
            adminId={adminId}
            handleCloseModal={closeModal}
            schoolOptions={schoolOptions}
          />
        )}
        {showModal === modalNames.bulkArchiveClassesModal && (
          <BulkArchiveClassesModal
            handleBulkArchiveClasses={handleBulkArchiveClasses}
            handleCloseModal={closeModal}
          />
        )}
        <h1>Account Management</h1>
        {accountManagementContent}
      </div>
    </main>
  );
};

export default AccountManagement;
